import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { RiDraggable } from '@remixicon/react';
import { Table } from '@@types/index';
import { Color } from '@resources/colors';
import { getTableStatus, TABLE_STATUS, TableStatus } from '@utils/tableStatus';
import { formatSessionTimeShortLabel, getElapsedPercent } from '@utils/tableTime';
import { colFlex, rowFlex } from '@styles/flexStyles';
import ProgressRing from '@components/admin/order/table-manage/common/ProgressRing';

const SELECTED_OUTLINE_PX = 3;
const SELECTED_OFFSET_PX = 2;
// 선택 아웃라인이 카드 밖으로 뻗는 폭 — overflow가 있는 부모는 이만큼 여유를 둬야 잘리지 않는다
export const SELECTED_RING_PX = SELECTED_OUTLINE_PX + SELECTED_OFFSET_PX;
const HANDLE_ICON_PX = 14;
const DIMMED_OPACITY = 0.28;
const CARD_PADDING_Y_PX = 8;
const CARD_PADDING_X_PX = 6;
const QUICK_START_BUTTON_HEIGHT_PX = 22;
const QUICK_START_TITLE = '‘사용 시작’을 클릭해야 이 테이블에서 주문을 받을 수 있습니다';

interface StatusStyle {
  background: string;
  border: string;
  shadow: string;
  number: string;
  time: string;
}

const STATUS_STYLE: Record<TableStatus, StatusStyle> = {
  [TABLE_STATUS.EMPTY]: { background: '#f4f4f4', border: Color.HEAVY_GREY, shadow: 'none', number: Color.MUTED_GREY, time: Color.MUTED_GREY },
  [TABLE_STATUS.USING]: {
    background: Color.WHITE,
    border: Color.BORDER_GREY,
    shadow: '0 2px 8px rgba(92, 92, 92, 0.12)',
    number: Color.GREY,
    time: Color.GREY,
  },
  [TABLE_STATUS.WARNING]: {
    background: Color.KIO_ORANGE_FAINT,
    border: '#FFD2A8',
    shadow: '0 2px 8px rgba(92, 92, 92, 0.08)',
    number: Color.GREY,
    time: Color.KIO_ORANGE_DARK,
  },
  [TABLE_STATUS.EXCEEDED]: {
    background: Color.RED,
    border: Color.RED,
    shadow: '0 2px 10px rgba(255, 90, 90, 0.35)',
    number: Color.WHITE,
    time: Color.WHITE,
  },
};

const exceededPulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.72; }
  100% { opacity: 1; }
`;

// 터치 기기는 탭이 곧 클릭이라 72px 카드에서 선택하려다 세션을 열기 쉽다. hover가 되는 기기에서만 버튼을 드러낸다
const quickStartReveal = css`
  &:focus-within [data-role='quick-start'] {
    opacity: 1;
    pointer-events: auto;
  }

  &:focus-within [data-role='status'] {
    opacity: 0;
  }

  @media (hover: hover) {
    &:hover [data-role='quick-start'] {
      opacity: 1;
      pointer-events: auto;
    }

    &:hover [data-role='status'] {
      opacity: 0;
    }
  }
`;

const Container = styled.div<{ status: TableStatus; isSelected: boolean; isDimmed: boolean; clickable: boolean; hasQuickStart: boolean }>`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  padding: ${CARD_PADDING_Y_PX}px ${CARD_PADDING_X_PX}px;
  position: relative;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'inherit')};
  opacity: ${({ isDimmed }) => (isDimmed ? DIMMED_OPACITY : 1)};
  transition: opacity 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  background-color: ${({ status }) => STATUS_STYLE[status].background};
  border: 1px solid ${({ status }) => STATUS_STYLE[status].border};
  box-shadow: ${({ status }) => STATUS_STYLE[status].shadow};
  outline: ${({ isSelected }) => (isSelected ? `${SELECTED_OUTLINE_PX}px solid ${Color.KIO_ORANGE}` : 'none')};
  outline-offset: ${SELECTED_OFFSET_PX}px;

  // 펄스가 opacity를 애니메이션하므로 dim 중엔 꺼야 필터 흐림이 살아남는다
  ${({ status, isDimmed }) =>
    status === TABLE_STATUS.EXCEEDED &&
    !isDimmed &&
    css`
      animation: ${exceededPulse} 2s infinite;
    `}
  ${colFlex()};
  ${({ hasQuickStart }) => hasQuickStart && quickStartReveal}
`;

const TopRow = styled.div`
  gap: 4px;
  ${rowFlex({ justify: 'space-between', align: 'start' })};
`;

const TableNumber = styled.span<{ status: TableStatus }>`
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${({ status }) => STATUS_STYLE[status].number};
`;

const OrderCountBadge = styled.span<{ status: TableStatus }>`
  min-width: 18px;
  height: 16px;
  box-sizing: border-box;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  background-color: ${({ status }) => (status === TABLE_STATUS.EXCEEDED ? Color.WHITE : Color.KIO_ORANGE)};
  color: ${({ status }) => (status === TABLE_STATUS.EXCEEDED ? Color.RED : Color.WHITE)};
  ${rowFlex({ justify: 'center', align: 'center' })};
`;

const HandleIcon = styled(RiDraggable)`
  width: ${HANDLE_ICON_PX}px;
  height: ${HANDLE_ICON_PX}px;
  flex-shrink: 0;
  color: ${Color.MUTED_GREY};
`;

const Bottom = styled.div<{ isHidden: boolean }>`
  margin-top: auto;
  min-width: 0;
  gap: 4px;
  opacity: ${({ isHidden }) => (isHidden ? 0 : 1)};
  transition: opacity 0.12s ease-in-out;
  ${rowFlex({ align: 'center' })};
`;

const QuickStartButton = styled.button<{ isStarting: boolean }>`
  position: absolute;
  left: ${CARD_PADDING_X_PX}px;
  right: ${CARD_PADDING_X_PX}px;
  bottom: ${CARD_PADDING_Y_PX}px;
  height: ${QUICK_START_BUTTON_HEIGHT_PX}px;
  padding: 0;
  border: none;
  border-radius: 999px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
  color: ${Color.WHITE};
  background-color: ${({ isStarting }) => (isStarting ? Color.HEAVY_GREY : Color.KIO_ORANGE)};
  cursor: ${({ isStarting }) => (isStarting ? 'default' : 'pointer')};
  opacity: ${({ isStarting }) => (isStarting ? 1 : 0)};
  pointer-events: none;
  transition: opacity 0.12s ease-in-out, background-color 0.12s ease-in-out;

  &:hover:not(:disabled) {
    background-color: ${Color.KIO_ORANGE_DARK};
  }
`;

// 카드 내용 폭 58px에서 링·간격을 뺀 38px 안에 "+10:30"(약 37.7px)까지 들어가야 한다
const TimeText = styled.span<{ status: TableStatus }>`
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
  color: ${({ status }) => STATUS_STYLE[status].time};
`;

function getRingColors(status: TableStatus): { fill: string; track: string } {
  if (status === TABLE_STATUS.EXCEEDED) return { fill: Color.WHITE, track: 'rgba(255, 255, 255, 0.35)' };
  return { fill: Color.KIO_ORANGE, track: Color.HEAVY_GREY };
}

interface TableLayoutCardProps {
  table: Table;
  orderCount?: number;
  isSelected?: boolean;
  isDimmed?: boolean;
  showHandle?: boolean;
  isStarting?: boolean;
  onSelect?: (table: Table) => void;
  onQuickStart?: (tableNumber: number) => void;
}

function TableLayoutCard({
  table,
  orderCount = 0,
  isSelected = false,
  isDimmed = false,
  showHandle = false,
  isStarting = false,
  onSelect,
  onQuickStart,
}: TableLayoutCardProps) {
  const status = getTableStatus(table);
  const ringColors = getRingColors(status);
  const hasQuickStart = status === TABLE_STATUS.EMPTY && Boolean(onQuickStart) && !showHandle && !isDimmed;
  const quickStartLabel = isStarting ? '시작 중' : '사용 시작';

  const handleClick = () => onSelect?.(table);

  const handleQuickStart = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onQuickStart?.(table.tableNumber);
  };

  return (
    <Container
      status={status}
      isSelected={isSelected}
      isDimmed={isDimmed}
      clickable={Boolean(onSelect)}
      hasQuickStart={hasQuickStart}
      title={hasQuickStart ? QUICK_START_TITLE : undefined}
      onClick={handleClick}
    >
      <TopRow>
        <TableNumber status={status}>{table.tableNumber}</TableNumber>
        {showHandle && <HandleIcon />}
        {!showHandle && orderCount > 0 && <OrderCountBadge status={status}>{orderCount}</OrderCountBadge>}
      </TopRow>
      <Bottom data-role="status" isHidden={hasQuickStart && isStarting}>
        <ProgressRing
          percent={getElapsedPercent(table.orderSession)}
          fillColor={ringColors.fill}
          trackColor={ringColors.track}
          holeColor={STATUS_STYLE[status].background}
        />
        <TimeText status={status}>{formatSessionTimeShortLabel(table.orderSession)}</TimeText>
      </Bottom>
      {hasQuickStart && (
        <QuickStartButton type="button" data-role="quick-start" isStarting={isStarting} disabled={isStarting} onClick={handleQuickStart}>
          {quickStartLabel}
        </QuickStartButton>
      )}
    </Container>
  );
}

export default TableLayoutCard;
